import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import {fetchMatterStatus} from "app/utils/constants/fetchMappingData";
import {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {fetchCountryData} from "../../api/countries";
import {get, post} from "../../common/network";
import FormSearchFilter from "../../components/common/form-search-filter/FormSearchFilter";
import AssignAttorney from "../../components/common/modal-preview/AssignAttorney";
import FilingIntroModal from "../../components/common/modal-preview/FilingIntroModal";
import MattersMuiTableWrapper from "../../components/common/mui-table-wrapper/MattersMuiTableWrapper";
import SidebarTagsDrawer from "../../components/common/sidebar-tags-wrapper/SidebarTagsDrawer";
import SidebarTagsWrapper from "../../components/common/sidebar-tags-wrapper/SidebarTagsWrapper";
import MultiFilter from "../../components/multi-filter/Multifilter";
import {PriorApplicationStatusOptions} from "../../constants";
import {DateFormatter, formatDate} from "../../constants/date_format";
import {normalizeDate} from "../../utils/CommonMethods";
import MatterDetailsModal from "./matter-details/MatterDetailsModal";
import ModifyMatterModal from "./modify-matter/ModifyMatterModal";


const MattersListing = () => {
    const [selectedRows, setSelectedRows] = useState([]); // Array of the selected rows
    const [filingModalOpen, setFilingModalOpen] = useState(false);
    const [isMatterDetailsOpen, setIsMatterDetailsOpen] = useState(false);
    const [isRowId, setIsRowId] = useState(null);
    const [isModifyMatterOpen, setIsModifyMatterOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [isFetching, setIsFetching] = useState(true);
    const [filterText, setFilterText] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [formTypeValue, setFormTypeValue] = useState("");
    const [dateValue, setDateValue] = useState("");
    const [matterStatusMapping, setMatterStatusMapping] = useState({});
    const [rowsSelection, setRowsSelection] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    const [sortedColumn, setSortedColumn] = useState("updated_at");
    const [sortDirection, setSortDirection] = useState("desc");
    const [countriesMapping, setCountriesMapping] = useState([]);
    const [isAssignAttorneyModalOpen, setIsAssignAttorneyModalOpen] = useState(false);
    const [dynamicFilters, setDynamicFilters] = useState([
        { column: '', operator: '', value: [], from: null, to: null }
    ]);
    const [searchResetKey, setSearchResetKey] = useState(0);
    const [filteredData, setFilteredData] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();

    const multiFilterColumns = [
        {
            field: "host_file_number",
            headerName: "Docket No.",
            type: "text",
        },
        {
            field: "application_number",
            headerName: "Application No.",
            type: "text",
        },
        {
            field: "status",
            headerName: "Status",
            type: "select",
            options: PriorApplicationStatusOptions,
        },
        {
            field: "filing_date",
            headerName: "Filing Date",
            type: "date",
        },
        {
            field: "grant_date",
            headerName: "Issue Date",
            type: "date",
        },
        {
            field: "updated_at",
            headerName: "Imported On",
            type: "date",
        },
        {
            field: "client",
            headerName: "Client",
            type: "text",
        },
        {
            field: "applicant",
            headerName: "Applicant",
            type: "text",
        },
        {
            field: "country_code",
            headerName: "Country",
            type: "select",
            options: countriesMapping,
        },
    ];

    useEffect(() => {
        if (location.state?.triggerRefresh) {
            fetchData().then(() => {
                navigate(location.pathname, {replace: true, state: null});
            });
        }
    }, [location]);

    const handleDownload = async () => {
        await downloadMatter();
    };

    const handleApplyFilters = (filters) => {
        // Clear single search state
        setSearchValue("");
        setFilterText("");
        setFormTypeValue("");
        setDateValue("");
        setSearchResetKey(prev => prev + 1);
        
        // Normalize and store filters
        const normalizedFilters = filters.map(f => ({
            ...f,
            from: normalizeDate(f.from),
            to: normalizeDate(f.to)
        }));
        
        setDynamicFilters(normalizedFilters);
        setPage(0);
        
        // Pass filters to fetchData
        fetchData({
            nextPage: 0,
            nextDynamicFilters: normalizedFilters
        });
    };

    const handleCloseAssignAttorneyModal = () => {
        setIsAssignAttorneyModalOpen(false);
    };

    //call download api
    const downloadMatter = async () => {
        try {
            setIsLoading(true);
            const selectedIds = [];
            for (const row of selectedRows) {
                selectedIds.push(row.id);
            }
            const response = await post(
                `docgen/matters/download`,
                {
                    matter_id: selectedIds
                },
                null,
                false
            );

            // Get the current timestamp
            const timestamp = new Date().getTime();

            const contentDispositionHeader =
                response.headers["Content-Disposition"];
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(contentDispositionHeader);
            const suggestedFilename =
                matches && matches[1]
                    ? matches[1].replace(/['"]/g, "")
                    : `matters-${timestamp}.csv`;
            // Create a Blob from the response content
            const blob = new Blob([response.data], {type: "text/csv"});

            // Create a URL for the blob response
            const url = URL.createObjectURL(blob);

            // Create a hidden anchor element
            const anchor = document.createElement("a");
            anchor.style.display = "none";
            anchor.href = url;
            anchor.download = suggestedFilename; // Set the desired file name

            // Append the anchor to the document body
            document.body.appendChild(anchor);

            // Simulate a click on the anchor to trigger the download
            anchor.click();
            setIsLoading(false);

            // Remove the anchor from the document body
            document.body.removeChild(anchor);
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    const columns = [
        {
            field: "host_file_number",
            header: "Docket No.",
            render: (value, row) => {
                return (
                    <div
                        style={{textDecoration: "underline", cursor: "pointer", paddingTop: "1%", paddingBottom: "1%"}}
                        onClick={() => {
                            setIsRowId(row.id);
                            setIsMatterDetailsOpen(true);
                        }}
                    >
                        {value}
                    </div>
                );
            }
        },
        {
            field: "application_number",
            header: "Application No."
        },
        {
            field: "status",
            header: "Status",
            render: value => matterStatusMapping[value] || value
        },
        {
            field: "filing_date",
            header: "Filing Date",
            render: value => {
                if (!value) return null;
                return formatDate(value, true);
            }
        },
        {
            field: "grant_date",
            header: "Issue Date",
            render: value => {
                if (!value) return null;
                return formatDate(value, true);
            }
        },
        {
            field: "updated_at",
            header: "Imported On",
            render: value => {
                const date = new Date(value)
                return DateFormatter(date);
            }
        },
        {
            field: "client",
            header: "Client",
            render: value => value || ""
        },
        {
            field: 'applicant',
            header: 'Applicant',
            render: value => value || ""
        },
        {
            field: "country_code",
            header: "Country",
            render: value => value || ""
        }
    ];

    const handleSearch = value => {
        setDynamicFilters([{ column: '', operator: '', value: [], from: null, to: null }]);
        const columnName = value?.value || ""; // Name of the column
        const searchValue = value?.searchedText || "";
        const dropdownValue = value?.dropdownValue || ""; // value selected in the dropdown field
        setSearchValue(searchValue);
        setFilterText(columnName);
        setFormTypeValue(dropdownValue);

        if (["updated_at", "filing_date", "grant_date"].includes(columnName)) {
            setDateValue(value);
        }
        setPage(0);
        fetchData({
            nextPage: 0,
            nextRowsPerPage: rowsPerPage,
            nextSortedColumn: sortedColumn,
            nextSortDirection: sortDirection,
            nextSearchValue: searchValue,
            nextFilterColumn: columnName,
            nextFormTypeValue: dropdownValue,
            nextDateValue: value
        });
    };
    const buildQueryParams = ({
        nextPage = page,
        nextRowsPerPage = rowsPerPage,
        nextSortedColumn = sortedColumn,
        nextSortDirection = sortDirection,
        nextSearchValue = searchValue,
        nextFilterColumn = filterText,
        nextFormTypeValue = formTypeValue,
        nextDateValue = dateValue,
        nextDynamicFilters = dynamicFilters
    } = {}) => {
        const params = new URLSearchParams();
        params.append("page", (nextPage + 1).toString());
        params.append("pageSize", nextRowsPerPage.toString());
        
        // Filter out empty filter objects
        const activeFilters = nextDynamicFilters.filter(f => 
            f.column && (
                f.operator === 'isEmpty' || 
                f.operator === 'isNotEmpty' ||
                (f.from || f.to) ||
                (Array.isArray(f.value) && f.value.length > 0) ||
                (typeof f.value === 'string' && f.value.trim())
            )
        );
        
        params.append("filters", JSON.stringify(activeFilters));

        // Only apply single search if no multi-filters are active
        if (activeFilters.length === 0) {
        let effectiveSearch = nextSearchValue;
        if (!effectiveSearch && nextFilterColumn === "status" && nextFormTypeValue) {
            effectiveSearch = nextFormTypeValue;
        }

        if (effectiveSearch) {
            params.append("search", effectiveSearch);

            if (nextFilterColumn && nextFilterColumn !== "all") {
                params.append("searchField", nextFilterColumn);
            }
        }

        if (["updated_at", "filing_date", "grant_date"].includes(nextFilterColumn) && nextDateValue) {
            const start = nextDateValue.startDate;
            const end = nextDateValue.endDate;

            const formatDate = (d) => {
                if (!d) return null;
                const dateObj = ["filing_date", "grant_date"].includes(nextFilterColumn) ? new Date(d + "T00:00:00") : new Date(d);
                const month = `${dateObj.getMonth() + 1}`.padStart(2, "0");
                const day = `${dateObj.getDate()}`.padStart(2, "0");
                const year = dateObj.getFullYear();
                return `${month}-${day}-${year}`;
            };

            const from = formatDate(start);
            const to = formatDate(end);

            if (nextFilterColumn === "updated_at") {
                if (from) params.append("updatedAtFrom", from);
                if (to) params.append("updatedAtTo", to);
            } else if (nextFilterColumn === "filing_date") {
                if (from) params.append("filingDateFrom", from);
                if (to) params.append("filingDateTo", to);
            } else if (nextFilterColumn === "grant_date") {
                if (from) params.append("grantDateFrom", from);
                if (to) params.append("grantDateTo", to);
            }
        }

        if (nextFilterColumn === "country_code" && nextFormTypeValue) {
            params.append("countryCode", nextFormTypeValue);
            }
        }

        if (nextSortedColumn) {
            const direction = nextSortDirection === "asc" ? "ASC" : "DESC";
            params.append("sort", `${nextSortedColumn}:${direction}`);
        }

        return params.toString();
    };
    const fetchData = async (options = {}) => {
        setIsFetching(true);
        try {
            const queryString = buildQueryParams(options);
            const response = await get(`docgen/matters?${queryString}`);
            setData(response?.data || []);
            const total = response.meta?.pagination?.total ?? 0;
            const apiPageSize = response.meta?.pagination?.pageSize ?? rowsPerPage;
            const apiPage = response.meta?.pagination?.page ?? (options.nextPage ?? page) + 1;
            setTotalCount(total);
            setRowsPerPage(apiPageSize);
            setPage(apiPage - 1);
        } catch (error) {
        } finally {
            setIsFetching(false);
        }
    };
    const fetchCountryMapping = async () => {
        const data = await fetchCountryData();
        if (data) {
            const countries = data
                .map(country => ({
                    label: country.name,
                    value: country.code,
                }))
                .sort((a, b) => a.label.localeCompare(b.label));

            setCountriesMapping(countries);
        }
    };
    useEffect(() => {
        const getMatterStatus = async () => {
            const response = await fetchMatterStatus();
            // const formResponse = await fetchGeneratedFormsData();
            // setAllGeneratedForms(formResponse);
            setMatterStatusMapping(response);
            await fetchData();
        };
        fetchCountryMapping();
        getMatterStatus();
    }, []);

    useEffect(() => {
        setFilteredData(data);
    }, [data]);

    return (
        <SidebarTagsWrapper
            active={selectedRows.length > 0}
            component={
                <SidebarTagsDrawer
                    upperSectionProps={{
                        title: "Docket Selected",
                        setSelectedRows: setSelectedRows,
                        selectedRows: selectedRows,
                        tagName: "doc_no"
                    }}
                    upperSubSectionProps={{
                        title: "Assign Attorney",
                        setIsAssignAttorneyModalOpen: setIsAssignAttorneyModalOpen
                    }}
                    buttonProps={{
                        title: "Select Form(s)",
                        onClick: () => {
                            navigate("/docgen/matters/forms", {state: {selectedRows}});
                        }
                    }}
                />
            }
        >
            <div className="page-content-wrapper">
                <Box className="w-100">
                    <header className="section-heading">
                        <div className="left-area">
                            <FormSearchFilter
                                options={[
                                    {value: "all", label: "All"},
                                    {value: "host_file_number", label: "Docket No."},
                                    {value: "application_number", label: "Application No."},
                                    {value: "status", label: "Status"},
                                    {value: "updated_at", label: "Imported On"},
                                    {value: "filing_date", label: "Filing Date"},
                                    {value: "grant_date", label: "Issue Date"},
                                    {value: "client", label: "Client"},
                                    {value: "applicant", label: "Applicant"},
                                    {value: "country_code", label: "Country"}
                                ]}
                                defaultOption={{value: "", label: "Select an option"}}
                                dateFiltersOptionsValueArray={["updated_at", "filing_date", "grant_date"]}
                                dropdownArray={["status", "country_code"]}
                                dropdownOptions={{
                                    status: PriorApplicationStatusOptions,
                                    country_code: countriesMapping
                                }}
                                onSearch={(values) => handleSearch(values)}
                                searchResetKey={searchResetKey}
                            />
                        </div>
                        <div className="right-area">
                            <Box sx={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center"}}>
                                <Box flexGrow={1}/>
                                {selectedRows.length === 1 &&
                                    <Box sx={{cursor: "pointer", marginRight: 1}} onClick={() => setIsModifyMatterOpen(true)}>
                                        <Button type="button" fullWidth>
                                            <EditNoteOutlinedIcon/>
                                        </Button>
                                        <p style={{
                                            textAlign: "center",
                                            color: "grey"
                                        }}>
                                            Modify
                                        </p>
                                    </Box>}
                                {selectedRows.length > 0 &&
                                    <Box sx={{cursor: "pointer",  marginRight: 1}} onClick={handleDownload}>
                                        {isLoading ? <CircularProgress sx={{color: "blue", marginLeft: "5%"}}/> : <>
                                            <Button type="button" fullWidth>
                                                <FileDownloadOutlinedIcon/>
                                            </Button>
                                            <p style={{
                                                textAlign: "center",
                                                color: "grey"
                                            }}>
                                                Download
                                            </p>
                                        </>}
                                    </Box>}
                                    <Box>
                                        <MultiFilter 
                                            filters={dynamicFilters}
                                            setFilters={setDynamicFilters}
                                            columns={multiFilterColumns}
                                            onAddFilter={handleApplyFilters} 
                                        />
                                    </Box>
                            </Box>
                        </div>
                    </header>
                    <MattersMuiTableWrapper
                        columns={columns}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        colgroup={[
                            {width: "11%"},
                            {width: "11%"},
                            {width: "11%"},
                            {width: "11%"},
                            {width: "11%"},
                            {width: "11%"},
                            {width: "15%"},
                            {width: "15%"}
                        ]}
                        data={filteredData}
                        isFetching={isFetching}
                        rowsSelection={rowsSelection}
                        setRowsSelection={setRowsSelection}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        totalCount={totalCount}
                        sortedColumn={sortedColumn}
                        sortDirection={sortDirection}
                        onPageChange={(event, newPage) => {
                            setPage(newPage);
                            fetchData({
                                nextPage: newPage,
                                nextDynamicFilters: dynamicFilters
                            });
                        }}
                        onRowsPerPageChange={(event) => {
                            const value = event.target?.value ?? 25;
                            setRowsPerPage(value);
                            setPage(0);
                            fetchData({
                                nextPage: 0,
                                nextRowsPerPage: value,
                                nextDynamicFilters: dynamicFilters
                            });
                        }}
                        onSortChange={(column) => {
                            if (column === sortedColumn) {
                                const nextDirection = sortDirection === "asc" ? "desc" : "asc";
                                setSortDirection(nextDirection);
                                fetchData({
                                    nextSortDirection: nextDirection,
                                    nextSortedColumn: column,
                                    nextDynamicFilters: dynamicFilters
                                });
                            } else {
                                setSortedColumn(column);
                                setSortDirection("asc");
                                fetchData({
                                    nextSortedColumn: column,
                                    nextSortDirection: "asc",
                                    nextDynamicFilters: dynamicFilters
                                });
                            }
                        }}
                    />
                </Box>
            </div>
            <MatterDetailsModal open={isMatterDetailsOpen} handleClose={() => setIsMatterDetailsOpen(false)} id={isRowId}/>
            {filingModalOpen && (<FilingIntroModal filingModalOpen={filingModalOpen} setFilingModalOpen={setFilingModalOpen} selectedRows={selectedRows}/>)}
            <ModifyMatterModal open={isModifyMatterOpen} selectedRows={selectedRows} handleClose={() => setIsModifyMatterOpen(false)}/>
            <AssignAttorney
                openModal={isAssignAttorneyModalOpen}
                closeModal={handleCloseAssignAttorneyModal}
                selectedDockets={selectedRows}
                setSelectedDockets={setSelectedRows}
            />
        </SidebarTagsWrapper>
    );
};


export default MattersListing;